// Pure label/formatting helpers for the Recurring tasks modal (feature-builds,
// July 2026) — anchor select options, ordinal day suffixes, and the mono
// cadence-pill text. Cadence maths itself lives in shared/recurrence.ts; this
// is presentation only, so it's kept client-side and unit-tested here.

import { WEEKLY_ANCHORS, type RecurrenceCadence } from '../../shared/recurrence';

export interface AnchorOption {
  value: string;
  label: string;
}

const WEEKDAY_LABELS: Record<(typeof WEEKLY_ANCHORS)[number], string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

/** "1st", "2nd", "3rd", "11th"…"13th", "21st"…"28th" — anchors never exceed
    28, but the 11–13 exception still falls inside that range. */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** Anchor select options for the given cadence — Mon…Sun for weekly,
    1st…28th + Last for monthly (backend stores '1'…'28' and 'last'). */
export function anchorOptions(cadence: RecurrenceCadence): AnchorOption[] {
  if (cadence === 'weekly') {
    return WEEKLY_ANCHORS.map((a) => ({ value: a, label: WEEKDAY_LABELS[a] }));
  }
  const days = Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: ordinal(i + 1) }));
  return [...days, { value: 'last', label: 'Last' }];
}

/** Sensible anchor to fall back to when cadence changes, so the pair always
    stays a valid combination for the backend. */
export function defaultAnchorForCadence(cadence: RecurrenceCadence): string {
  return cadence === 'weekly' ? 'mon' : '1';
}

/** Display label for a single anchor value (e.g. the current select value). */
export function anchorLabel(cadence: RecurrenceCadence, anchor: string): string {
  if (cadence === 'weekly') return WEEKDAY_LABELS[anchor as (typeof WEEKLY_ANCHORS)[number]] ?? anchor;
  if (anchor === 'last') return 'Last';
  const n = Number(anchor);
  return Number.isFinite(n) && n > 0 ? ordinal(n) : anchor;
}

/** Mono cadence-pill text ("weekly · mon", "monthly · 1st", "monthly ·
    last") — the .pill class uppercases it, so raw case doesn't matter. */
export function cadencePillText(cadence: RecurrenceCadence, anchor: string): string {
  if (cadence === 'weekly') return `weekly · ${anchor}`;
  return `monthly · ${anchor === 'last' ? 'last' : ordinal(Number(anchor))}`;
}
